<template>
  <div class="code-editor-container" :class="{ 'fullscreen': isFullscreen }">
    <div class="editor-toolbar">
      <a-button 
        type="text" 
        size="small" 
        @click="toggleFullscreen"
        class="fullscreen-btn"
      >
        <template #icon>
          <FullscreenOutlined v-if="!isFullscreen" />
          <FullscreenExitOutlined v-else />
        </template>
        {{ isFullscreen ? '退出全屏' : '全屏' }}
      </a-button>
    </div>
    <div ref="editorContainer" class="code-editor" :style="{ height: isFullscreen ? 'calc(100vh - 40px)' : height + 'px' }"></div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons-vue';
// 直接导入 monaco-editor，因为使用了 monaco-editor-webpack-plugin
// webpack plugin 会将 Monaco Editor 资源打包到本地，不需要使用 loader 从 CDN 加载
import * as monaco from 'monaco-editor';

export default {
  name: 'CodeEditor',
  components: {
    FullscreenOutlined,
    FullscreenExitOutlined
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: 'text'
    },
    height: {
      type: Number,
      default: 400
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const editorContainer = ref(null);
    const isFullscreen = ref(false);
    let editor = null;

    // 语言映射
    const languageMap = {
      xml: 'xml',
      csv: 'plaintext',
      md: 'markdown',
      json: 'json',
      text: 'plaintext'
    };

    // 初始化编辑器
    const initEditor = () => {
      if (!editorContainer.value) return;

      // Monaco Editor 已经通过 webpack plugin 打包，直接使用
      // monaco 对象已经可用，不需要异步加载
      const language = languageMap[props.language] || 'plaintext';

      editor = monaco.editor.create(editorContainer.value, {
        value: props.modelValue || '',
        language: language,
        theme: 'vs',
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        fontSize: 14,
        lineNumbers: 'on',
        wordWrap: 'on',
        readOnly: props.readOnly,
        formatOnPaste: true,
        formatOnType: true,
        // 启用工具栏（操作栏）
        contextmenu: true, // 启用右键菜单
        quickSuggestions: true, // 启用快速建议
        suggestOnTriggerCharacters: true, // 在触发字符时显示建议
        acceptSuggestionOnEnter: 'on', // 按 Enter 接受建议
        tabCompletion: 'on', // 启用 Tab 补全
        wordBasedSuggestions: true, // 基于单词的建议
        // 格式化相关
        formatOnType: true,
        formatOnPaste: true
      });

      // 监听内容变化
      editor.onDidChangeModelContent(() => {
        const value = editor.getValue();
        emit('update:modelValue', value);
        emit('change', value);
      });
    };

    // 更新语言
    const updateLanguage = (newLanguage) => {
      if (!editor) return;
      const language = languageMap[newLanguage] || 'plaintext';
      monaco.editor.setModelLanguage(editor.getModel(), language);
    };

    // 更新内容
    const updateValue = (newValue) => {
      if (!editor) return;
      const currentValue = editor.getValue();
      if (currentValue !== newValue) {
        editor.setValue(newValue || '');
      }
    };

    watch(() => props.language, (newLang) => {
      updateLanguage(newLang);
    });

    watch(() => props.modelValue, (newValue) => {
      updateValue(newValue);
    });

    // 全屏切换
    const toggleFullscreen = () => {
      if (!isFullscreen.value) {
        // 进入全屏
        const container = editorContainer.value?.parentElement?.parentElement;
        if (container) {
          if (container.requestFullscreen) {
            container.requestFullscreen();
          } else if (container.webkitRequestFullscreen) {
            container.webkitRequestFullscreen();
          } else if (container.mozRequestFullScreen) {
            container.mozRequestFullScreen();
          } else if (container.msRequestFullscreen) {
            container.msRequestFullscreen();
          }
        }
        isFullscreen.value = true;
      } else {
        // 退出全屏
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        isFullscreen.value = false;
      }
    };

    // 监听全屏状态变化
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      isFullscreen.value = isCurrentlyFullscreen;
      
      // 全屏状态改变时，重新布局编辑器
      if (editor) {
        setTimeout(() => {
          editor.layout();
        }, 100);
      }
    };

    onMounted(() => {
      initEditor();
      
      // 监听全屏状态变化
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', handleFullscreenChange);
      document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    });

    onBeforeUnmount(() => {
      // 移除全屏事件监听
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      
      // 如果处于全屏状态，退出全屏
      if (isFullscreen.value) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
      
      if (editor) {
        editor.dispose();
      }
    });

    return {
      editorContainer,
      isFullscreen,
      toggleFullscreen
    };
  }
};
</script>

<style scoped>
.code-editor-container {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.code-editor-container.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: #fff;
  border: none;
  border-radius: 0;
}

.editor-toolbar {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.fullscreen-btn {
  display: flex;
  align-items: center;
  gap: 4px;
}

.code-editor {
  width: 100%;
}
</style>

